import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Grid, Typography, TextField, FormHelperText } from '@mui/material';
import { Field, getIn, FormikProps } from 'formik';
import { CheckboxComponent } from '../../../components/inputComponents/CheckboxComponent';
import { GridItems } from '../../../components/assignPromotionFlowComponent/GridItems';
import {
    IRenderMixCodeComponents,
} from '../../../types/componentTypes/mixcodes-types';
import MultiSelectAutocomplete from '../../../common/MultiSelectAutocomplete/MultiSelectAutocomplete';
import { useParams } from 'react-router-dom';
import { API } from 'aws-amplify';
import { getPrizesForConfig } from '../../../../src/graphql/queries';
import { SelectOption } from '../../../components/selectComponents/SelectOption';

const alphaNumericRegex = /^[a-zA-Z0-9]+$/;

interface MixCodesPrizeProps {
    formik: FormikProps<any>;
    disableMixCodesPrizeMapping: boolean;
}

interface AddStringValueParams {
    label: string;
    index: number;
    dynamicKey?: string;
}

export interface prizeDataType {
    name: string;
    prize_id: string;
}

const MixCodesPrizeMapping: React.FC<MixCodesPrizeProps> = ({
    formik,
    disableMixCodesPrizeMapping,
    }) => {
    const { promotionId } = useParams<{ promotionId: string }>();
    const { values } = formik;
    const [prizes, setPrizes] = useState<Array<prizeDataType>>([]);
    const redirectUrl = useMemo(
        () => `/addPrize/${values.promotionId}/${values.configurationId}/`,
        [values.promotionId, values.configurationId]
    );
    const normalizeIds = (ids?: string[]): string[] => Array.isArray(ids) ? ids.filter(Boolean) : [];
    const mappingPath = 'params.mixCodesPrizeMapping.mapping';

    useEffect(() => {
        let isMounted = true;
        const fetchPrizes = async () => {
            try {
                const queryResult: any = await API.graphql({
                    query: getPrizesForConfig,
                    variables: { configuration_id: promotionId },
                });
                const prizesForConfig = queryResult?.data?.getPrizesForConfig?.items || [];
                if (isMounted) {
                    setPrizes(prizesForConfig);
                }
            } catch (e) {
                console.log("error", e);
            }
        };
        fetchPrizes();
        return () => {
            isMounted = false;
        };
    }, [promotionId]);

    useEffect(() => {
        const codeType = formik.values.tempParams.codeType?.[0];
        let mapping: any[] = [];

        if (codeType === 'Pincodes' && formik.values.flow && formik.values.flow.instantWin?.params?.winningPrizesPerPinCodes) {

            const pinCodes = formik.values.flow.instantWin.params.winningPrizesPerPinCodes;

            Object.entries(pinCodes).forEach(([programId, lots]) => {
                Object.entries(lots).forEach(([lotId, prizes]) => {
                    mapping.push({ pincodes: lotId, prizes: Array.isArray(prizes) ? prizes : [] });
                });
            });

        }
        if (codeType === 'Viral Codes' && formik.values.flow && formik.values.flow.instantWin?.params?.winningPrizesPerViralCode) {
            const viralCodes = formik.values.flow.instantWin.params.winningPrizesPerViralCode;

            Object.entries(viralCodes).forEach(([viralCode, prizes]) => {
                mapping.push({ viralCode, prizes: Array.isArray(prizes) ? prizes : [] });
            });
        }

    }, [formik.values.tempParams.codeType, formik.values.flow]);

    const didMountRef = useRef(false);

    useEffect(() => {
        if (formik.values.tempParams.codeType?.[0] !== 'Pincodes') return;
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        const lotIds = normalizeIds(formik.values.params.lotIds);
        const current: any[] = getIn(formik.values, mappingPath) ?? [];
        const filtered = current.filter(row => lotIds.includes(row.pincodes));
        const hasAnyValue = filtered.some(row => typeof row.pincodes === 'string' && row.pincodes.trim() !== '');
        if (filtered.length !== current.length || !hasAnyValue) {
            formik.setFieldValue(mappingPath, filtered);
            if (!hasAnyValue) {
                formik.setFieldValue('tempParams.mixCodesPrizeMapping', false);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formik.values.params.lotIds,
        formik.values.tempParams.codeType,
    ]);

    const MAX_ROWS = 10;

    const handlePrizeMappingCheckbox = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked;

            const isPin = formik.values.tempParams.codeType?.[0] === 'Pincodes';
            const key = isPin ? 'pincodes' : 'viralCode';

            const defaultRow = [{ [key]: '', prizes: [] }];

            const currentMapping = getIn(formik.values, mappingPath) as any[] | undefined;

            formik.setFieldValue('tempParams.mixCodesPrizeMapping', checked);
            formik.setFieldValue(
                mappingPath,
                checked
                ?
                    currentMapping && currentMapping.length > 0
                    ? currentMapping
                    : defaultRow
                :
                    []
            );
            },
        [formik]
    );


    const validateMixCode = useCallback(
        (value = ''): string | undefined => {
            const trimmed = value.trim();
            if (!trimmed) return 'This field is required.';
            if (!alphaNumericRegex.test(trimmed)) return 'Only alphanumeric characters allowed';
            return undefined;
        },
        []
    );

    const addStringValue = useCallback(
        (params: AddStringValueParams) => {
        const currentArray = getIn(formik.values, params.label) || [];
        if (currentArray.length >= MAX_ROWS) {
            return;
        }
        const newRow: any = { prizes: [] };
            if (params.dynamicKey) {
                newRow[params.dynamicKey] = '';
            }
            formik.setFieldValue(params.label, [...currentArray, newRow]);
            },
        [formik]
    );

    const removeValue = useCallback(
        (params: { label: string; index: number }) => {
            const currentArray = getIn(formik.values, params.label) || [];
            const newArray = currentArray.filter((_: any, i: number) => i !== params.index);
            formik.setFieldValue(params.label, newArray);
        },
        [formik]
    );

    const renderMixCodePrizeMapping = ({
        mixCodesConfigurationParam,
        titleText,
        items,
    }: IRenderMixCodeComponents) =>
    items.map((item: any, i: number) => {
        const rowUid = item.uid || i;
        const selectedPrizeIds = (getIn(formik.values, mappingPath+`[${i}].prizes`) || [])
                .map((prize: any) =>
                    typeof prize === 'object' && prize !== null ? prize.prize_id : prize
                )
                .filter((id: string) => id);
        const backendPrizeIds = prizes.map((p) => p.prize_id).filter((id) => id);
        const availablePrizeIds = Array.from(new Set([...backendPrizeIds, ...selectedPrizeIds]));
        const prizeMappingError = getIn(formik.errors, mappingPath+`[${i}].prizes`);
        let dynamicMixCodesConfigurationParam = formik.values.tempParams.codeType[0] === 'Pincodes' ? 'pincodes' : 'viralCode';
        const normalizedLotIds = normalizeIds(formik.values.params?.lotIds);
        const fieldPath = `params.mixCodesPrizeMapping.mapping[${i}].${mixCodesConfigurationParam}`;

        return (
            <GridItems
                key={rowUid}
                formik={formik}
                index={i}
                component={
                    <Grid container direction="column" spacing={2}>
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="flex-start" sx={{ flexWrap: 'nowrap' }}>
                                <Grid item xs={7}>
                                    <Typography variant="caption">{titleText}</Typography>

                                    {dynamicMixCodesConfigurationParam === "pincodes" && (
                                        (() => {
                                            const optionsList = [...normalizedLotIds].filter(opt => typeof opt === 'string');
                                            const mappingArray = formik.values.params.mixCodesPrizeMapping?.mapping || [];
                                            const selectedLotIds = mappingArray.map((row) => row.pincodes).filter((val, idx) => idx !== i && !!val);
                                            return (
                                            <>
                                                <SelectOption
                                                    formik={formik}
                                                    formikValue={`${mappingPath}[${i}].${mixCodesConfigurationParam}`}
                                                    touchedField="__never__"
                                                    customAction={(newId: string, index: number) => {
                                                        formik.setFieldTouched(fieldPath, true);
                                                        if (typeof newId !== 'string') return;
                                                            const existing = formik.values.params.mixCodesPrizeMapping?.mapping ?? [];
                                                            const updated = [...existing];
                                                            updated[index] = {...updated[index],[mixCodesConfigurationParam]: newId };
                                                            formik.setFieldValue(`params.mixCodesPrizeMapping.mapping`,updated
                                                        );
                                                    }}
                                                    customParam={{ index: i }}
                                                    disabled={false}
                                                    optionsList={optionsList}
                                                    selectedValues={selectedLotIds}
                                                    titleVariant="caption"
                                                    data-testid={`prize-mapping-lot-id-select`}
                                                />
                                            </>
                                            );
                                        })()
                                    )}
                                    {dynamicMixCodesConfigurationParam === "viralCode" && (
                                        <>
                                            <Field
                                                fullWidth
                                                data-testid={`${mixCodesConfigurationParam}[${i}]-input`}
                                                key={`${mixCodesConfigurationParam}[${i}]-input`}
                                                as={TextField}
                                                id={`mixCodesPrizeMapping-${mixCodesConfigurationParam}-input-${rowUid}`}
                                                name={mappingPath+`[${i}].${mixCodesConfigurationParam}`}
                                                validate={(value: string) => validateMixCode(value)}
                                                error={Boolean(
                                                    getIn(
                                                        formik.errors,
                                                        mappingPath+`[${i}].${mixCodesConfigurationParam}`
                                                    )
                                                )}
                                            />
                                        </>
                                    )}
                                <Grid item xs={5}></Grid>
                                    <FormHelperText
                                        key={`${mixCodesConfigurationParam}${i}-helper`}
                                        data-testid={`${mixCodesConfigurationParam}[${i}]-error`}
                                        error={Boolean(
                                            getIn(
                                                formik.errors,
                                                mappingPath+`[${i}].${mixCodesConfigurationParam}`
                                            )
                                        )}
                                    >
                                        {getIn(
                                            getIn(
                                                formik.touched,
                                                mappingPath+`[${i}].${mixCodesConfigurationParam}`
                                            ) && formik.errors,
                                            mappingPath+`[${i}].${mixCodesConfigurationParam}`
                                        )}
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={5}>
                                <MultiSelectAutocomplete
                                    width="200px"
                                    ids={getIn(formik.values, mappingPath+`[${i}].prizes`) || []}
                                    placeholder="Select Prize"
                                    labelVariant="caption"
                                    label="Prize Id"
                                    onChange={(newIDs: string[]) => {
                                        formik.setFieldValue(
                                            `params.mixCodesPrizeMapping.mapping[${i}].prizes`,
                                            newIDs
                                        );
                                    }}
                                    showAddNewButton={false}
                                    options={availablePrizeIds}
                                    redirectUrlOnAdd={redirectUrl}
                                    dataTestId="add-campaign-prize-mapping-id"
                                    error={prizeMappingError || ''}
                                    maxSelection={MAX_ROWS}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
                addAction={(params: { label: string; index: number }) =>
                    addStringValue({ ...params, dynamicKey: dynamicMixCodesConfigurationParam })
                }
                removeAction={removeValue}
                customParam={{ label: mappingPath }}
                formikValueArray={mappingPath}
            />
        );
    });

return (
        <>
        <CheckboxComponent
            fieldName="tempParams.mixCodesPrizeMapping"
            label="Mix Codes prize mapping"
            disabledCondition={disableMixCodesPrizeMapping}
            customAction={handlePrizeMappingCheckbox}
        />
        {formik.values.tempParams.mixCodesPrizeMapping && (
           <Grid container className="gridPincodeOrigin">
            {Array.isArray(formik.values.params.mixCodesPrizeMapping.mapping) &&
            formik.values.params.mixCodesPrizeMapping.mapping.length > 0 && (() => {
                const isPin = formik.values.tempParams.codeType[0] === 'Pincodes';
                const titleText = isPin ? 'Enter Lot Id' : 'Enter Viral Code';
                const mixCodesConfigurationParam = isPin ? 'pincodes' : 'viralCode';
                return renderMixCodePrizeMapping({
                    items: formik.values.params.mixCodesPrizeMapping.mapping,
                    titleText,
                    mixCodesConfigurationParam,
                });
            })()
            }
        </Grid>
        )}

        </>
    );
};

export default MixCodesPrizeMapping;
